// src/views/ShippingPackages.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ShippingPackages.css';
import { FaPlus, FaSearch, FaTimes, FaEdit, FaTrash, FaUndo, FaSave, FaSort, FaSortUp, FaSortDown, FaDownload, FaUpload, FaFileCsv } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;

// localStorage keys for persistence
const LS_SORT_KEY = 'shipkg.sort.v1';

const unitPairs = {
  dim: ['IN','CM'],
  wt:  ['LBS','KG']
};

function vol(l,w,h) {
  const a = Number(l)||0, b = Number(w)||0, c = Number(h)||0;
  const v = a*b*c; return v ? +v.toFixed(2) : '';
}
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : '';
};

export default function ShippingPackages() {
  // table data
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // filters
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);

  // CSV import/export helpers
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // clients
  const [clients, setClients] = useState([]);

  // drawer/edit
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [fieldErrors, setFieldErrors] = useState({});

  // sort (persisted)
  const getInitialSort = () => {
    try {
      const raw = localStorage.getItem(LS_SORT_KEY);
      if (!raw) return { sortField: 'name', sortOrder: 'ASC' };
      const j = JSON.parse(raw);
      return { sortField: j.sortField || 'name', sortOrder: j.sortOrder === 'DESC' ? 'DESC' : 'ASC' };
    } catch { return { sortField: 'name', sortOrder: 'ASC' }; }
  };
  const [sortField, setSortField] = useState(getInitialSort().sortField);
  const [sortOrder, setSortOrder] = useState(getInitialSort().sortOrder);

  useEffect(() => {
    localStorage.setItem(LS_SORT_KEY, JSON.stringify({ sortField, sortOrder }));
  }, [sortField, sortOrder]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Package Definitions', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  function blankForm() {
    return {
      clientid: '',
      name: '',
      code: '',
      length: '',
      width: '',
      height: '',
      dim_unit: 'IN',
      max_weight: '',
      weight_unit: 'LBS',
      inner_length: '',
      inner_width: '',
      inner_height: '',
      notes: '',
      active: 1
    };
  }

  // net helpers
  const fetchJSON = async (url, opts) => {
    const r = await fetch(url, opts);
    if (!r.ok) {
      let m = `${r.status} ${r.statusText}`;
      try { const j = await r.json(); if (j?.error) m = j.error; } catch {}
      throw new Error(m);
    }
    return r.json();
  };

  // ===== CSV helpers (template, export, import) via backend =====
  const handleDownloadTemplate = () => {
    // Use backend endpoint to ensure correct headers/order
    window.location.href = `${API}/api/shipping-packages/template.csv`;
  };

  const handleExportCSV = () => {
    // Preserve current filters in export
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (clientId) params.set('clientid', String(clientId));
    if (activeOnly) params.set('active', '1');
    if (showDeleted) params.set('deleted', '1'); // server default is non-deleted
    const url = `${API}/api/shipping-packages/export?${params.toString()}`;
    window.location.href = url;
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // Optional: if a client is selected, pass as default for blank clientid fields
      if (clientId) form.append('default_clientid', String(clientId));
      const res = await fetch(`${API}/api/shipping-packages/import`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data?.error || 'Import failed');
      }
      await loadRows();
      if (data?.errors?.length) {
        alert(`Import complete: inserted ${data.inserted}, updated ${data.updated}, skipped ${data.skipped}.
Errors:
- ${data.errors.slice(0, 10).join('\n- ')}${data.errors.length > 10 ? '\n…' : ''}`);
      }
    } catch (err) {
      alert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // lookups + list
  const loadClients = async () => {
    try {
      // For client users, lock and avoid internal-only endpoint
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const isClient = (u?.isclient === true) || (String(u?.isclient).toLowerCase() === 'true') || (String(u?.isclient) === '1') || (Number(u?.isclient) === 1);
      const lockedId = u?.clientid ?? u?.clientId ?? '';
      const lockedName = u?.clientname || u?.clientName || (lockedId ? `Client #${lockedId}` : '');
      if (isClient && lockedId) { setClients([{ id: String(lockedId), name: lockedName }]); return; }
      const j = await fetchJSON(`${API}/api/clients?limit=1000`);
      const list = Array.isArray(j?.data) ? j.data : [];
      setClients(list.map(c => ({ id: c.id, name: c.name })));
    } catch { setClients([]); }
  };

  const loadRows = async () => {
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (clientId) p.set('clientid', clientId);
      if (activeOnly) p.set('active', '1');
      p.set('deleted', showDeleted ? '1' : '0');

      const j = await fetchJSON(`${API}/api/shipping-packages?${p.toString()}`);
      setRows(Array.isArray(j?.data) ? j.data : []);
      setMeta(j?.meta || { total: (j?.data || []).length });
    } catch (e) { setErr(e.message); setRows([]); setMeta({ total: 0 }); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { loadRows(); }, [q, clientId, activeOnly, showDeleted]);

  // sorting (client-side)
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortOrder === 'ASC' ? 1 : -1;
    arr.sort((a, b) => {
      let A = a[sortField], B = b[sortField];
      // basic normalization
      if (typeof A === 'string') A = A.toLowerCase();
      if (typeof B === 'string') B = B.toLowerCase();
      if (A > B) return dir;
      if (A < B) return -dir;
      return 0;
    });
    return arr;
  }, [rows, sortField, sortOrder]);

  // sort handlers/icons (Receipts style)
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('ASC');
    }
  };
  const renderSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortOrder === 'ASC'
      ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
      : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />;
  };

  // --- Row selection (checkboxes) ---
  const [selected, setSelected] = useState(new Set());
  const allIds = useMemo(() => sortedRows.map(r => r.id), [sortedRows]);
  const allChecked = selected.size > 0 && selected.size === allIds.length;
  const someChecked = selected.size > 0 && selected.size < allIds.length;

  const toggleOne = (id, checked) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (checked) s.add(id); else s.delete(id);
      return s;
    });
  };
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(allIds) : new Set());
  };
  const clearSelection = () => setSelected(new Set());

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} package(s)?`)) return;
    try {
      for (const id of selected) {
        await fetchJSON(`${API}/api/shipping-packages/${id}`, { method: 'DELETE' });
      }
      clearSelection();
      await loadRows();
    } catch (e) {
      alert(e.message);
    }
  };

  // CRUD
  const startCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setFieldErrors({});
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      clientid: row.clientid ?? '',
      name: row.name || '',
      code: row.code || '',
      length: row.length ?? '',
      width: row.width ?? '',
      height: row.height ?? '',
      dim_unit: row.dim_unit || 'IN',
      max_weight: row.max_weight ?? '',
      weight_unit: row.weight_unit || 'LBS',
      inner_length: row.inner_length ?? '',
      inner_width: row.inner_width ?? '',
      inner_height: row.inner_height ?? '',
      notes: row.notes || '',
      active: Number(row.active ? 1 : 0)
    });
    setFieldErrors({});
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.code.trim()) e.code = 'Code is required';
    ['length','width','height'].forEach(k=>{
      const v = Number(form[k]); if (!Number.isFinite(v) || v <= 0) e[k] = 'Must be > 0';
    });
    if (form.max_weight !== '' && (!Number.isFinite(Number(form.max_weight)) || Number(form.max_weight) < 0)) {
      e.max_weight = 'Must be >= 0';
    }
    return e;
  };

  const save = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    const payload = {
      ...form,
      clientid: form.clientid === '' ? null : Number(form.clientid),
      length: Number(form.length),
      width: Number(form.width),
      height: Number(form.height),
      max_weight: form.max_weight === '' ? null : Number(form.max_weight),
      inner_length: form.inner_length === '' ? null : Number(form.inner_length),
      inner_width: form.inner_width === '' ? null : Number(form.inner_width),
      inner_height: form.inner_height === '' ? null : Number(form.inner_height),
      active: Number(form.active ? 1 : 0),
    };

    try {
      if (editingId) {
        await fetchJSON(`${API}/api/shipping-packages/${editingId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      } else {
        await fetchJSON(`${API}/api/shipping-packages`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      }
      setOpen(false);
      setEditingId(null);
      await loadRows();
    } catch (e) {
      alert(e.message);
    }
  };

  const softDelete = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try { await fetchJSON(`${API}/api/shipping-packages/${id}`, { method: 'DELETE' }); await loadRows(); }
    catch (e) { alert(e.message); }
  };
  const restore = async (id) => {
    try { await fetchJSON(`${API}/api/shipping-packages/${id}/restore`, { method: 'PUT' }); await loadRows(); }
    catch (e) { alert(e.message); }
  };

  // volume helpers (display)
  const renderVolumeFt3 = (r) => {
    // Server returns volume_ft3; if not, fall back to convert
    if (r.volume_ft3 != null) return r.volume_ft3;
    const raw = vol(r.length, r.width, r.height);
    if (!raw) return '';
    const in3 = (r.dim_unit || 'IN') === 'IN' ? raw : raw / 16.387064; // CM³→IN³
    const ft3 = in3 / 1728;
    return +ft3.toFixed(3);
  };

  return (
    <div className="shipkg-wrapper">
      <div className="shipkg-left-pane">
        <div className="shipkg-toolbar">
          {/* search */}
          <div className="shipkg-search">
            <FaSearch />
            <input
              placeholder="Search name / code"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              onKeyDown={(e)=>{ if (e.key==='Enter') loadRows(); }}
            />
            {q && <button className="clear" onClick={()=>setQ('')}><FaTimes/></button>}
          </div>

          <label className="shipkg-filter">
            Client:
            <select value={clientId} onChange={(e)=>setClientId(e.target.value)}>
              <option value="">All</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <label className="shipkg-filter">
            <input type="checkbox" checked={activeOnly} onChange={e=>setActiveOnly(e.target.checked)} />
            Active only
          </label>

          <label className="shipkg-filter">
            <input type="checkbox" checked={showDeleted} onChange={e=>setShowDeleted(e.target.checked)} />
            Show deleted
          </label>

          <div className="shipkg-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn danger" onClick={deleteSelected} disabled={!selected.size} title="Delete selected rows">
              <FaTrash style={{ marginRight: 6 }} /> Delete Selected
            </button>
            <button className="btn primary" onClick={startCreate}><FaPlus/> New</button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />
            <button className="btn" onClick={handleDownloadTemplate} title="Download CSV template">
              <FaFileCsv style={{ marginRight: 6 }} /> Template
            </button>
            <button className="btn" onClick={handleImportClick} title="Import CSV" disabled={importing}>
              <FaUpload style={{ marginRight: 6 }} /> {importing ? 'Importing…' : 'Import'}
            </button>
            <button className="btn" onClick={handleExportCSV} title="Export CSV">
              <FaDownload style={{ marginRight: 6 }} /> Export
            </button>
          </div>
        </div>

        <div className="shipkg-tablewrap">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : err ? (
            <div className="loading" style={{ color:'#ff6b6b' }}>{err}</div>
          ) : (
            <table className="shipkg-table">
              <thead>
                <tr>
                  <th className="sel" style={{ width: 44 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked; }}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th style={{ width: 130 }}>Actions</th>
                  <th onClick={()=>handleSort('clientid')} className={sortField==='clientid'?'active-sort':''}>
                    Client {renderSortIcon('clientid')}
                  </th>
                  <th onClick={()=>handleSort('name')} className={sortField==='name'?'active-sort':''}>
                    Name {renderSortIcon('name')}
                  </th>
                  <th onClick={()=>handleSort('code')} className={sortField==='code'?'active-sort':''}>
                    Code {renderSortIcon('code')}
                  </th>
                  <th onClick={()=>handleSort('length')} className={sortField==='length'?'active-sort num':'num'}>
                    L {renderSortIcon('length')}
                  </th>
                  <th onClick={()=>handleSort('width')} className={sortField==='width'?'active-sort num':'num'}>
                    W {renderSortIcon('width')}
                  </th>
                  <th onClick={()=>handleSort('height')} className={sortField==='height'?'active-sort num':'num'}>
                    H {renderSortIcon('height')}
                  </th>
                  <th onClick={()=>handleSort('dim_unit')} className={sortField==='dim_unit'?'active-sort':''}>
                    Unit {renderSortIcon('dim_unit')}
                  </th>
                  <th onClick={()=>handleSort('max_weight')} className={sortField==='max_weight'?'active-sort num':'num'}>
                    Max Wt {renderSortIcon('max_weight')}
                  </th>
                  <th onClick={()=>handleSort('weight_unit')} className={sortField==='weight_unit'?'active-sort':''}>
                    Wt Unit {renderSortIcon('weight_unit')}
                  </th>
                  <th onClick={()=>handleSort('volume_ft3')} className={sortField==='volume_ft3'?'active-sort num':'num'}>
                    Volume (ft³) {renderSortIcon('volume_ft3')}
                  </th>
                  <th onClick={()=>handleSort('active')} className={sortField==='active'?'active-sort':''}>
                    Active {renderSortIcon('active')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.length ? sortedRows.map(r => (
                  <tr key={r.id} className={Number(r.deleted) ? 'deleted' : ''}>
                    <td className="nowrap sel">
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.name}`}
                        checked={selected.has(r.id)}
                        onChange={(e) => toggleOne(r.id, e.target.checked)}
                      />
                    </td>
                    <td className="nowrap">
                      {!Number(r.deleted) ? (
                        <>
                          <button className="mini-btn" onClick={()=>startEdit(r)} title="Edit"><FaEdit/></button>{' '}
                          <button className="mini-btn danger" onClick={()=>softDelete(r.id)} title="Delete"><FaTrash/></button>
                        </>
                      ) : (
                        <button className="mini-btn" onClick={()=>restore(r.id)} title="Restore"><FaUndo/></button>
                      )}
                    </td>
                    <td>{r.clientid ? (clients.find(c=>String(c.id)===String(r.clientid))?.name || r.clientid) : 'All'}</td>
                    <td>{r.name}</td>
                    <td>{r.code}</td>
                    <td className="num">{r.length}</td>
                    <td className="num">{r.width}</td>
                    <td className="num">{r.height}</td>
                    <td>{r.dim_unit}</td>
                    <td className="num">{r.max_weight ?? '—'}</td>
                    <td>{r.weight_unit}</td>
                    <td className="num">{renderVolumeFt3(r) ?? '—'}</td>
                    <td>{Number(r.active) ? 'Yes' : 'No'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={13} style={{ color:'#999' }}>No packages.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Drawer */}
      {open && (
        <div className="shipkg-drawer" onClick={()=>setOpen(false)}>
          <div className="shipkg-panel" onClick={(e)=>e.stopPropagation()}>
            <div className="shipkg-panel-header">
              <h3>{editingId ? 'Edit Package' : 'New Package'}</h3>
              <button className="icon-btn" onClick={()=>setOpen(false)}><FaTimes/></button>
            </div>

            <div className="shipkg-form">
              <label>
                <span>Client</span>
                <select value={form.clientid} onChange={(e)=>setForm(f=>({...f, clientid: e.target.value}))}>
                  <option value="">All</option>
                  {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>

              <label>
                <span>Name *</span>
                <input value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} className={fieldErrors.name?'err':''}/>
              </label>

              <label>
                <span>Code *</span>
                <input value={form.code} onChange={e=>setForm(f=>({...f, code:e.target.value}))} className={fieldErrors.code?'err':''}/>
              </label>

              <div className="grid3">
                <label>
                  <span>Length *</span>
                  <input type="number" step="0.01" value={form.length} onChange={e=>setForm(f=>({...f, length:e.target.value}))} className={fieldErrors.length?'err':''}/>
                </label>
                <label>
                  <span>Width *</span>
                  <input type="number" step="0.01" value={form.width} onChange={e=>setForm(f=>({...f, width:e.target.value}))} className={fieldErrors.width?'err':''}/>
                </label>
                <label>
                  <span>Height *</span>
                  <input type="number" step="0.01" value={form.height} onChange={e=>setForm(f=>({...f, height:e.target.value}))} className={fieldErrors.height?'err':''}/>
                </label>
              </div>

              <label>
                <span>Dimension Unit</span>
                <select value={form.dim_unit} onChange={e=>setForm(f=>({...f, dim_unit:e.target.value}))}>
                  {unitPairs.dim.map(u=><option key={u} value={u}>{u}</option>)}
                </select>
              </label>

              <div className="grid2">
                <label>
                  <span>Max Weight</span>
                  <input type="number" step="0.01" value={form.max_weight} onChange={e=>setForm(f=>({...f, max_weight:e.target.value}))} className={fieldErrors.max_weight?'err':''}/>
                </label>
                <label>
                  <span>Weight Unit</span>
                  <select value={form.weight_unit} onChange={e=>setForm(f=>({...f, weight_unit:e.target.value}))}>
                    {unitPairs.wt.map(u=><option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
              </div>

              <div className="grid3">
                <label>
                  <span>Inner L</span>
                  <input type="number" step="0.01" value={form.inner_length} onChange={e=>setForm(f=>({...f, inner_length:e.target.value}))}/>
                </label>
                <label>
                  <span>Inner W</span>
                  <input type="number" step="0.01" value={form.inner_width} onChange={e=>setForm(f=>({...f, inner_width:e.target.value}))}/>
                </label>
                <label>
                  <span>Inner H</span>
                  <input type="number" step="0.01" value={form.inner_height} onChange={e=>setForm(f=>({...f, inner_height:e.target.value}))}/>
                </label>
              </div>

              <label>
                <span>Notes</span>
                <input value={form.notes} onChange={e=>setForm(f=>({...f, notes:e.target.value}))}/>
              </label>

              <label className="chk">
                <input type="checkbox" checked={!!form.active} onChange={e=>setForm(f=>({...f, active: e.target.checked?1:0}))}/>
                <span>Active</span>
              </label>

              <div className="row muted">
                Volume (computed): <b>{vol(form.length, form.width, form.height) ? (form.dim_unit==='IN'
                  ? ( (Number(form.length)*Number(form.width)*Number(form.height))/1728 ).toFixed(3)
                  : ( (Number(form.length)*Number(form.width)*Number(form.height))/28316.846592 ).toFixed(3)
                ) : '—'}</b> ft³
              </div>
            </div>

            <div className="shipkg-panel-footer">
              <button className="btn" onClick={()=>setOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={save}><FaSave/> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
